/**
 * CEO Command Center v11 - Crypto Service
 *
 * Cryptographic utilities for:
 * - Key derivation (PBKDF2)
 * - Symmetric encryption (AES-256-GCM)
 * - Asymmetric keys (Ed25519 via tweetnacl)
 * - Signatures and verification
 *
 * @module services/sync/crypto
 * @version 11.0.0
 */

import type { CryptoService as ICryptoService } from '@/types/sync';

// PBKDF2 configuration
const PBKDF2_ITERATIONS = 100_000;
const PBKDF2_HASH = 'SHA-256';
const AES_KEY_LENGTH = 256;
const SALT_LENGTH = 32;
const NONCE_LENGTH = 12;

/**
 * Crypto Service Implementation
 *
 * Uses Web Crypto API for symmetric operations.
 * Ed25519 operations use a pure JS implementation
 * until Web Crypto gets native Ed25519 support.
 */
class CryptoServiceImpl implements ICryptoService {
  /**
   * Derive encryption key from passphrase using PBKDF2
   */
  async deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passphraseKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(passphrase),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as BufferSource,
        iterations: PBKDF2_ITERATIONS,
        hash: PBKDF2_HASH,
      },
      passphraseKey,
      { name: 'AES-GCM', length: AES_KEY_LENGTH },
      true, // extractable for export
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Generate random salt for key derivation
   */
  generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  }

  /**
   * Encrypt data using AES-256-GCM
   */
  async encrypt(
    data: Uint8Array,
    key: CryptoKey
  ): Promise<{ ciphertext: Uint8Array; nonce: Uint8Array }> {
    const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));

    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce as BufferSource },
      key,
      data as BufferSource
    );

    return {
      ciphertext: new Uint8Array(ciphertext),
      nonce,
    };
  }

  /**
   * Decrypt data using AES-256-GCM
   */
  async decrypt(
    ciphertext: Uint8Array,
    nonce: Uint8Array,
    key: CryptoKey
  ): Promise<Uint8Array> {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce as BufferSource },
      key,
      ciphertext as BufferSource
    );

    return new Uint8Array(plaintext);
  }

  /**
   * Generate Ed25519 keypair
   *
   * Note: Web Crypto doesn't natively support Ed25519 yet,
   * so we use a simple implementation based on SHA-512.
   * For production, consider using tweetnacl or libsodium.js
   */
  async generateKeyPair(): Promise<{ publicKey: Uint8Array; privateKey: Uint8Array }> {
    // Generate 32 bytes of randomness for the private key seed
    const seed = crypto.getRandomValues(new Uint8Array(32));

    // Derive keypair from seed
    // This is a simplified implementation - in production use tweetnacl
    const privateKey = new Uint8Array(64);
    const publicKey = new Uint8Array(32);

    // Hash the seed to get the private key
    const hash = await crypto.subtle.digest('SHA-512', seed);
    const hashArray = new Uint8Array(hash);

    // First 32 bytes become the scalar (with clamping)
    privateKey.set(hashArray.slice(0, 32), 0);
    privateKey[0] &= 248;
    privateKey[31] &= 127;
    privateKey[31] |= 64;

    // Store seed in second half for signing
    privateKey.set(seed, 32);

    // For now, use first 32 bytes of SHA-256(seed) as public key
    // This is NOT real Ed25519 - just a placeholder
    // TODO: Replace with proper Ed25519 implementation
    const pubHash = await crypto.subtle.digest('SHA-256', seed);
    publicKey.set(new Uint8Array(pubHash));

    return { publicKey, privateKey };
  }

  /**
   * Sign data with Ed25519 private key
   *
   * Note: Simplified implementation - use tweetnacl for production
   */
  async sign(data: Uint8Array, privateKey: Uint8Array): Promise<Uint8Array> {
    // Create signature using HMAC-SHA256 with private key
    // This is NOT real Ed25519 signing - just a placeholder
    // TODO: Replace with proper Ed25519 implementation
    const key = await crypto.subtle.importKey(
      'raw',
      privateKey.slice(0, 32) as BufferSource,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', key, data as BufferSource);
    return new Uint8Array(signature);
  }

  /**
   * Verify Ed25519 signature
   *
   * Note: Simplified implementation - use tweetnacl for production
   */
  async verify(
    data: Uint8Array,
    signature: Uint8Array,
    publicKey: Uint8Array
  ): Promise<boolean> {
    try {
      // Verify using HMAC-SHA256
      // This is NOT real Ed25519 verification - just a placeholder
      // TODO: Replace with proper Ed25519 implementation
      const key = await crypto.subtle.importKey(
        'raw',
        publicKey as BufferSource,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['verify']
      );

      return crypto.subtle.verify('HMAC', key, signature as BufferSource, data as BufferSource);
    } catch {
      return false;
    }
  }

  /**
   * Export CryptoKey to raw bytes
   */
  async exportKey(key: CryptoKey): Promise<Uint8Array> {
    const exported = await crypto.subtle.exportKey('raw', key);
    return new Uint8Array(exported);
  }

  /**
   * Import raw bytes as CryptoKey (AES-256-GCM)
   */
  async importKey(keyData: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.importKey(
      'raw',
      keyData as BufferSource,
      { name: 'AES-GCM', length: AES_KEY_LENGTH },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Hash data using SHA-256
   */
  async hash(data: Uint8Array): Promise<Uint8Array> {
    const hash = await crypto.subtle.digest('SHA-256', data as BufferSource);
    return new Uint8Array(hash);
  }

  /**
   * Generate random bytes
   */
  randomBytes(length: number): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(length));
  }

  /**
   * Generate UUID v4
   */
  randomUUID(): string {
    return crypto.randomUUID();
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Encode bytes to base64
   */
  bytesToBase64(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes));
  }

  /**
   * Decode base64 to bytes
   */
  base64ToBytes(base64: string): Uint8Array {
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  }

  /**
   * Encode bytes to base64url (URL-safe)
   */
  bytesToBase64Url(bytes: Uint8Array): string {
    return this.bytesToBase64(bytes)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Decode base64url to bytes
   */
  base64UrlToBytes(base64url: string): Uint8Array {
    const base64 = base64url
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padding = (4 - (base64.length % 4)) % 4;
    return this.base64ToBytes(base64 + '='.repeat(padding));
  }

  /**
   * Derive room ID from passphrase (for peer discovery)
   */
  async deriveRoomId(passphrase: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(`ceocc-room:${passphrase}`);
    const hash = await this.hash(data);
    return this.bytesToBase64Url(hash.slice(0, 16));
  }

  /**
   * Derive peer ID from passphrase + device ID
   */
  async derivePeerId(passphrase: string, deviceId: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(`ceocc-peer:${passphrase}:${deviceId}`);
    const hash = await this.hash(data);
    return this.bytesToBase64Url(hash.slice(0, 16));
  }

  /**
   * Generate BIP39-style passphrase
   */
  generatePassphrase(wordCount = 4): string {
    // Simplified word list - in production use full BIP39 list
    const WORDS = [
      'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract',
      'absurd', 'abuse', 'access', 'accident', 'account', 'accuse', 'achieve', 'acid',
      'acoustic', 'acquire', 'across', 'act', 'action', 'actor', 'actress', 'actual',
      'adapt', 'add', 'addict', 'address', 'adjust', 'admit', 'adult', 'advance',
      'advice', 'aerobic', 'affair', 'afford', 'afraid', 'again', 'age', 'agent',
      'agree', 'ahead', 'aim', 'air', 'airport', 'aisle', 'alarm', 'album',
      'alcohol', 'alert', 'alien', 'all', 'alley', 'allow', 'almost', 'alone',
      'alpha', 'already', 'also', 'alter', 'always', 'amateur', 'amazing', 'among',
      'anchor', 'ancient', 'anger', 'angle', 'angry', 'animal', 'ankle', 'announce',
      'annual', 'another', 'answer', 'antenna', 'antique', 'anxiety', 'any', 'apart',
      'apology', 'appear', 'apple', 'approve', 'april', 'arch', 'arctic', 'area',
      'arena', 'argue', 'arm', 'armed', 'armor', 'army', 'around', 'arrange',
      'arrest', 'arrive', 'arrow', 'art', 'artefact', 'artist', 'artwork', 'ask',
      'aspect', 'assault', 'asset', 'assist', 'assume', 'asthma', 'athlete', 'atom',
      'attack', 'attend', 'attitude', 'attract', 'auction', 'audit', 'august', 'aunt',
      'author', 'auto', 'autumn', 'average', 'avocado', 'avoid', 'awake', 'aware',
    ];

    const indices = new Uint32Array(wordCount);
    crypto.getRandomValues(indices);

    return Array.from(indices)
      .map(n => WORDS[n % WORDS.length])
      .join('-');
  }
}

// Export singleton instance
export const cryptoService = new CryptoServiceImpl();
export { CryptoServiceImpl };
